import { closeAllModals, openModal } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsDemoSession } from '/@/renderer/config/demo-config';
import { useDeleteSong } from '/@/renderer/features/songs/mutations/delete-song-mutation';
import {
    showTracksDeletedToast,
    showTracksDeletingToast,
} from '/@/renderer/features/trash/api/track-trash';
import { useCurrentServerId } from '/@/renderer/store';
import { ContextMenu } from '/@/shared/components/context-menu/context-menu';
import { ConfirmModal } from '/@/shared/components/modal/modal';
import { Text } from '/@/shared/components/text/text';
import { toast } from '/@/shared/components/toast/toast';
import { Song } from '/@/shared/types/domain-types';

interface DeleteSongActionProps {
    disabled?: boolean;
    items: Song[];
}

interface DeleteSongConfirmProps {
    items: Song[];
    serverId: string;
}

// The modal closes on Confirm; a toast says what's being deleted while pymix works
// through the batch, which takes a few seconds even for one track (#183).
const DeleteSongConfirm = ({ items, serverId }: DeleteSongConfirmProps) => {
    const { t } = useTranslation();
    const deleteSongMutation = useDeleteSong({});
    const queryClient = useQueryClient();

    const handleDeleteSong = useCallback(async () => {
        closeAllModals();
        const subboxIds = items
            .map((song) => {
                const id = song.tags?.subboxid?.[0] || song.tags?.subbox_id?.[0];
                if (!id) {
                    console.warn(
                        '[DeleteSong] No subboxid found for song:',
                        song.name,
                        'Available tags:',
                        Object.keys(song.tags || {}),
                    );
                }
                return id;
            })
            .filter((id): id is string => !!id);

        if (subboxIds.length === 0) {
            console.error(
                '[DeleteSong] No subboxid found for any selected tracks. Items:',
                items.map((s) => ({ name: s.name, tags: s.tags })),
            );
            toast.error({
                message: 'No subboxid found for the selected tracks',
                title: t('error.genericError', { postProcess: 'sentenceCase' }),
            });
            return;
        }

        // Named when it's one track, in the toasts as in the confirm.
        const name = items.length === 1 && subboxIds.length === 1 ? items[0].name : undefined;
        const hideDeleting = showTracksDeletingToast({ count: subboxIds.length, name });
        try {
            const result = await deleteSongMutation
                .mutateAsync({
                    ids: subboxIds,
                    serverId,
                    songIds: items.map((song) => song.id),
                })
                .finally(hideDeleting);

            // Into the trash (#152): the toast carries an Undo.
            if (result.trash_batch_id) {
                showTracksDeletedToast({
                    batchId: result.trash_batch_id,
                    count: subboxIds.length,
                    name,
                    queryClient,
                    serverId,
                });
            } else {
                toast.success({
                    message:
                        subboxIds.length > 1
                            ? `${subboxIds.length} tracks deleted`
                            : t('form.deleteSong.success', { postProcess: 'sentenceCase' }),
                });
            }
        } catch (err: any) {
            // A partial failure still deleted some tracks (and the mutation's
            // onSettled already invalidated the list). Tell the user how many
            // failed and why, rather than a blanket error.
            const results: Array<{ success: boolean }> | undefined = err?.data?.results;
            if (results?.length) {
                const failedCount = results.filter((r) => !r.success).length;
                const okCount = results.length - failedCount;
                toast.error({
                    message:
                        okCount > 0
                            ? `Deleted ${okCount}, failed ${failedCount}: ${err.message}`
                            : err.message,
                    title: t('error.genericError', { postProcess: 'sentenceCase' }),
                });
            } else {
                toast.error({
                    message: err.message,
                    title: t('error.genericError', { postProcess: 'sentenceCase' }),
                });
            }
        }
    }, [deleteSongMutation, items, queryClient, serverId, t]);

    return (
        <ConfirmModal onConfirm={handleDeleteSong}>
            {/* Names what goes and where it goes (design §11.4). Play counts survive
                a restore (PurgeMissing = "never", pymix#210), so nothing warns of them. */}
            <Text>
                {items.length > 1
                    ? t('form.trash.tracks.confirm', { count: items.length })
                    : t('form.trash.tracks.confirmOne', { name: items[0]?.name })}
            </Text>
        </ConfirmModal>
    );
};

export const DeleteSongAction = ({ disabled, items }: DeleteSongActionProps) => {
    const { t } = useTranslation();
    const serverId = useCurrentServerId();
    // pymix's DELETE /track 403s the `demo` account (only demoadmin's own session can
    // delete from the shared library), so there's no point offering the action here —
    // matches how upload/watch are hidden rather than left to fail, in sync-mode-placeholder.
    const isDemo = useIsDemoSession();

    const openDeleteSongModal = useCallback(() => {
        if (items.length === 0 || !serverId) return;

        openModal({
            children: <DeleteSongConfirm items={items} serverId={serverId} />,
            title: t('form.deleteSong.title', { postProcess: 'sentenceCase' }),
        });
    }, [items, serverId, t]);

    if (items.length === 0 || isDemo) return null;

    return (
        <ContextMenu.Item disabled={disabled} leftIcon="remove" onSelect={openDeleteSongModal}>
            {t('action.deleteSong', { postProcess: 'sentenceCase' })}
        </ContextMenu.Item>
    );
};
